import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import moment from 'moment';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Building2, Calendar, Clock, DollarSign, Percent } from 'lucide-react';
import { API_BASE_URL } from '../config';

const API = API_BASE_URL + '/api';
const CORPORATE_API_KEY = 'corp_api_key_alvorubon_2024_secure';

function CorporateDashboard() {
  const [kennitala, setKennitala] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [corporateData, setCorporateData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!kennitala || kennitala.replace(/[^0-9]/g, '').length !== 10) {
      toast.error('Vinsamlegast sláðu inn gilt 10 stafa kennitölu');
      return;
    }

    try {
      setLoading(true);
      
      // Check if corporate customer
      const response = await axios.get(
        `${API}/portal/corporate-check?kennitala=${kennitala}`,
        {
          headers: { 'X-API-Key': CORPORATE_API_KEY }
        }
      );

      if (!response.data.is_corporate) {
        toast.error('Fyrirtækið er ekki skráð sem fyrirtækjaviðskiptavinur');
        return;
      }

      setCorporateData(response.data);
      setAuthenticated(true);
      localStorage.setItem('corporate_kennitala', kennitala);
      
      // Load jobs
      await loadJobs(kennitala);
      
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Villa við innskráningu');
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async (kt) => {
    try {
      const response = await axios.get(
        `${API}/portal/corporate-jobs?kennitala=${kt}`,
        {
          headers: { 'X-API-Key': CORPORATE_API_KEY }
        }
      );
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Tókst ekki að hlaða bókun um');
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setCorporateData(null);
    setJobs([]);
    setKennitala('');
    localStorage.removeItem('corporate_kennitala');
  };

  // Auto-login if kennitala in localStorage
  useEffect(() => {
    const savedKt = localStorage.getItem('corporate_kennitala');
    if (savedKt) {
      setKennitala(savedKt);
      // Auto-check
      handleLogin({ preventDefault: () => {} });
    }
  }, []);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center">
            <div className="mx-auto w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4">
              <Building2 className="h-10 w-10 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Fyrirtækja Portal</CardTitle>
            <p className="text-blue-100 text-sm mt-2">Skráðu inn kennitölu fyrirtækis</p>
          </CardHeader>

          <CardContent className="p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kennitala fyrirtækis
                </label>
                <Input
                  type="text"
                  value={kennitala}
                  onChange={(e) => setKennitala(e.target.value)}
                  placeholder="1234567890"
                  maxLength={11}
                  className="text-center text-lg"
                  required
                />
                <p className="text-xs text-slate-500 mt-1 text-center">10 tölustafir</p>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 py-6 text-base flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Athuga...
                  </>
                ) : (
                  <>
                    <Building2 className="h-5 w-5 mr-2" />
                    Innskrá
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t text-center space-y-2">
              <p className="text-sm text-slate-600">Ekki með aðgang?</p>
              <a 
                href="/corporate-apply"
                className="inline-block text-blue-600 hover:text-blue-700 hover:underline font-medium text-sm transition-colors"
              >
                Sækja um fyrirtækjaþjónustu →
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated view
  const statusConfig = {
    queued: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Í bið' },
    in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Í vinnslu' },
    completed: { bg: 'bg-green-50', text: 'text-green-700', label: 'Lokið' },
    cancelled: { bg: 'bg-red-50', text: 'text-red-700', label: 'Hætt við' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <Card className="shadow-xl border-2 border-blue-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">{corporateData.company_name}</h1>
                  <p className="text-sm text-slate-600">Kennitala: {corporateData.kennitala}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="flex items-center gap-2 text-green-600 font-bold">
                    <Percent className="h-5 w-5" />
                    <span className="text-2xl">{corporateData.discount}%</span>
                  </div>
                  <p className="text-xs text-slate-500">Fyrirtækja afsláttur</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                >
                  Útskrá
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Heildar bókanir</p>
                  <p className="text-3xl font-bold text-blue-600">{jobs.length}</p>
                </div>
                <Calendar className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Virkar bókanir</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {jobs.filter(j => j.status === 'queued' || j.status === 'in_progress').length}
                  </p>
                </div>
                <Clock className="h-10 w-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Heildarupphæð</p>
                  <p className="text-3xl font-bold text-green-600">
                    {jobs.reduce((sum, j) => sum + j.total, 0).toLocaleString()} kr
                  </p>
                </div>
                <DollarSign className="h-10 w-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Jobs Table */}
        <Card>
          <CardHeader>
            <CardTitle>📋 Bókanir</CardTitle>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🚗</div>
                <p className="text-slate-500">Engar bókanir fundust</p>
                <p className="text-sm text-slate-400 mt-2">
                  Hafðu samband til að bóka þjónustu
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dagsetning</TableHead>
                      <TableHead>Tími</TableHead>
                      <TableHead>Bílnúmer</TableHead>
                      <TableHead>Þjónusta</TableHead>
                      <TableHead>Staða</TableHead>
                      <TableHead className="text-right">Verð</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map(job => {
                      const status = statusConfig[job.status] || statusConfig.queued;
                      return (
                        <TableRow key={job.id}>
                          <TableCell className="font-medium">
                            {moment(job.date).format('DD.MM.YYYY')}
                          </TableCell>
                          <TableCell>{job.time || '-'}</TableCell>
                          <TableCell className="font-bold">{job.plate_number}</TableCell>
                          <TableCell>{job.service_name}</TableCell>
                          <TableCell>
                            <Badge className={`${status.bg} ${status.text} border-0`}>
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold text-blue-600">
                            {job.total.toLocaleString()} kr
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3">📞 Hafa samband</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-600">Netfang:</p>
                <a href="mailto:info@alvorubon.is" className="text-blue-600 hover:underline font-medium">
                  info@alvorubon.is
                </a>
              </div>
              <div>
                <p className="text-slate-600">Sími:</p>
                <a href="tel:+3545812345" className="text-blue-600 hover:underline font-medium">
                  581-2345
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CorporateDashboard;
